import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { pincodeValidator } from '../util/auth.util';
import { AuthLayoutComponent } from '../ui/auth-layout/auth-layout.component';
import { PinCodeComponent } from '../ui/pin-code/pin-code.component';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../data/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LoaderComponent } from "src/app/shared/ui-common/loader/loader.component";

enum Step {
  CreateNewPin = 'create-new-pin',
  ConfirmNewPin = 'confirm-new-pin',
  Loading = 'loading',
  Done = 'done',
}

@Component({
  selector: 'app-feature-forgot-code',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AuthLayoutComponent,
    PinCodeComponent,
    TranslatePipe,
    LoaderComponent
],
  templateUrl: './feature-forgot-code.component.html',
  styleUrls: ['./feature-forgot-code.component.scss', '../ui/auth.styles.scss'],
})
export class FeatureForgotCodeComponent implements OnInit {
  forgotCodeForm: FormGroup;
  steps = Step;
  step = signal<Step>(Step.CreateNewPin);
  pincodeNotMatched = signal<boolean>(false);
  token: string | null = null;
  isTransitioning = false;
  forgotCodeEmailValidationErrorSignal = signal<string | null>(null);
  newPasswordValidationErrorSignal = signal<string | null>(null);

  get pincodeControl(): AbstractControl<string> | null {
    return this.forgotCodeForm.get('pincode');
  }

  get errorMessage(): string {
    if (this.forgotCodeForm.controls['pincode'].errors && this.forgotCodeForm.controls['pincode'].touched) {
      if (this.forgotCodeForm.controls['pincode'].errors['invalidPincode']) {
        return 'WebApp.v1.Auth.ForgotCode.Error.Not4Digit';
      } else if (this.forgotCodeForm.controls['pincode'].errors['repeatingDigits']) {
        return 'WebApp.v1.Auth.ForgotCode.Error.RepeatingDigits';
      } else if (this.forgotCodeForm.controls['pincode'].errors['sequentialDigits']) {
        return 'WebApp.v1.Auth.ForgotCode.Error.SequentialDigits';
      }
    } else if (this.pincodeNotMatched()) {
      return 'WebApp.v1.Auth.ForgotCode.Error.NotMatched';
    }

    return '';
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private router: Router
  ) {
    this.forgotCodeForm = new FormGroup({
      pincode: new FormControl({ value: '', disabled: true }, [Validators.required, pincodeValidator()]),
    });
  }

  ngOnInit(): void {
    const token = this.activatedRoute.snapshot.paramMap.get('token');

    if (token) {
      this.authService.onValidateResetPasswordToken(token).subscribe({
        next: () => {
          this.forgotCodeForm.get('pincode')?.enable();
          this.token = token;
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.forgotCodeEmailValidationErrorSignal.set(errorResponse.error);
        },
      });
    }
  }

  goToNextStep(newStep: Step): void {
    this.isTransitioning = true;

    setTimeout(() => {
      this.step.set(newStep);
      this.isTransitioning = false;
    }, 400);
  }

  updatePincode(pin: string): void {
    if (this.pincodeControl?.errors) {
      this.pincodeControl.setValue('');
      this.pincodeControl.setErrors(null);
      this.pincodeControl.updateValueAndValidity({ onlySelf: true });
    }

    if (pin.length === 4) {
      this.submitPincode(pin);
    }
  }

  submitPincode(pin: string): void {
    if (this.step() === Step.CreateNewPin) {
      this.forgotCodeForm.get('pincode')?.setValue(pin);

      if (this.forgotCodeForm.controls['pincode'].errors) {
        this.pincodeControl?.updateValueAndValidity({ onlySelf: true });
        this.pincodeControl?.markAsTouched();

        return;
      }

      this.goToNextStep(Step.ConfirmNewPin);
    } else if (this.step() === Step.ConfirmNewPin) {
      if (this.forgotCodeForm.get('pincode')?.value === pin) {
        const model = {
          pin: this.forgotCodeForm.get('pincode')?.value,
          pin2: pin
        };
        this.step.set(Step.Loading);
        this.authService.onChangePassword(this.token as string, model).subscribe({
          next: () => {
            this.step.set(Step.Done);
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 3000);
          },
          error: (errorResponse: HttpErrorResponse) => {
            this.newPasswordValidationErrorSignal.set(errorResponse.error);
          }
        });
      } else {
        this.pincodeNotMatched.set(true);
      }
    }
  }

  goToCreateNewPin(): void {
    this.goToNextStep(Step.CreateNewPin);
    this.pincodeNotMatched.set(false);
    this.forgotCodeForm.get('pincode')?.setValue('');
    this.forgotCodeForm.get('pincode')?.markAsUntouched();
  }
}
